import { ADMIN_ROLES, type AdminRole } from '@sbc/domain-models/admin';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api/client';
import { useCreateAdminUser } from '@/lib/api/users';
import { ROLE_LABELS } from '@/lib/auth/roles';

export function UserNewPage() {
  const createUser = useCreateAdminUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('admin');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await createUser.mutateAsync({ email, role });
      void navigate({ to: '/users' });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the user.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Add admin</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          They receive a welcome email to set their password.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New admin</CardTitle>
          <CardDescription>Invite by email address.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="new-admin@seoulbeautyclub.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(value) =>
                  setRole(ADMIN_ROLES.find((option) => option === value) ?? role)
                }
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_ROLES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {ROLE_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={createUser.isPending}>
                {createUser.isPending ? 'Inviting…' : 'Invite admin'}
              </Button>
              <Button asChild variant="outline" type="button">
                <Link to="/users">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
